import { Button, Input, Row, Table } from "antd";
import dayjs from "dayjs";
import { InputNumber, MultiStateCheckbox } from "../../Components";
import detailTableDisabledStyles from "../../shared/detailTableDisabled.module.scss";
import { checkPermissions } from "../../utils/checkPermissions";
import {
  getOrderCostReviewHighlightStatus,
} from "./orderCostReviewHighlight";
import {
  buildOrderCostReviewPayload,
  getAllowedOrderCostReviewStatuses,
  isOrderCostReviewPayloadAllowed,
  isOrderCostReviewableLine,
} from "./helpers";
import styles from "./OrderCostSheetReviewHighlight.module.scss";
import {
  OrderCostDetail,
  OrderCostLine,
  OrderCostReviewer,
  OrderCostReviewStatus,
} from "./types";

type OrderCostSheetDetailProps = {
  dataDetail: OrderCostDetail | null;
  loadingDetail: boolean;
  savingDetail: boolean;
  features: any;
  footerButtonClassName?: string;
  showWorkflowActions: boolean;
  onClose: () => void;
  onChangeDetail: (
    configId: string | number,
    dataIndex: string | (string | number)[],
    value: any,
  ) => void;
  onSaveDetail: () => void;
  onSubmitLevel1: () => void;
  onReview: (
    level: 1 | 2,
    reviewer: OrderCostReviewer,
  ) => boolean | Promise<boolean>;
};

export const OrderCostSheetDetail = ({
  dataDetail,
  loadingDetail,
  savingDetail,
  features,
  footerButtonClassName,
  showWorkflowActions,
  onClose,
  onChangeDetail,
  onSaveDetail,
  onSubmitLevel1,
  onReview,
}: OrderCostSheetDetailProps) => {
  if (!dataDetail) return null;

  const canEditIncurred = checkPermissions("INCURRED", "edit", features);
  const canApproveSale = checkPermissions("INCURRED", "approve", features);
  const canApproveManager = checkPermissions(
    "INCURRED",
    "approve_l2",
    features,
  );
  const canRejectReview = checkPermissions("INCURRED", "reject", features);
  const saleReviewPermissions = {
    canApprove: canApproveSale,
    canReject: canRejectReview,
  };
  const managerReviewPermissions = {
    canApprove: canApproveManager,
    canReject: canRejectReview,
  };
  const saleAllowedStatuses = getAllowedOrderCostReviewStatuses(
    saleReviewPermissions,
  );
  const managerAllowedStatuses = getAllowedOrderCostReviewStatuses(
    managerReviewPermissions,
  );
  const canSubmitSaleReview =
    (canApproveSale || canRejectReview) &&
    isOrderCostReviewPayloadAllowed(
      buildOrderCostReviewPayload(dataDetail.lines ?? [], "sale"),
      saleReviewPermissions,
    );
  const canSubmitManagerReview =
    (canApproveManager || canRejectReview) &&
    isOrderCostReviewPayloadAllowed(
      buildOrderCostReviewPayload(dataDetail.lines ?? [], "manager"),
      managerReviewPermissions,
    );

  const getReviewRowClassName = (record: OrderCostLine) => {
    const highlightStatus = getOrderCostReviewHighlightStatus(
      record,
      dataDetail?.state,
    );

    switch (highlightStatus) {
      case "PENDING":
        return styles.ReviewPendingRow;
      case "REQUEST_EDIT":
        return styles.ReviewRequestEditRow;
      case "REJECTED":
        return styles.ReviewRejectedRow;
      default:
        return "";
    }
  };

  const renderReviewNoteInput = (
    record: OrderCostLine,
    reviewer: OrderCostReviewer,
    status?: OrderCostReviewStatus | null,
  ) => {
    const isSaleReviewer = reviewer === "sale";
    const canEditByState = isSaleReviewer
      ? dataDetail?.state === "WAITING_APPROVE_L1" &&
        canRejectReview
      : dataDetail?.state === "WAITING_APPROVE_L2" &&
        canRejectReview;
    const canEditByStatus =
      status === "REJECTED" || status === "REQUEST_EDIT";

    return (
      <Input.TextArea
        rows={2}
        style={{ marginTop: 8 }}
        value={record.reviews?.[reviewer]?.note ?? ""}
        onChange={(event) =>
          onChangeDetail(record.configId, ["reviews", reviewer, "note"], event)
        }
        disabled={!canEditByState || !canEditByStatus}
      />
    );
  };

  const detailColumns = [
    {
      title: "",
      dataIndex: "name",
      fixed: false,
      hidden: false,
      render: (row: any, record: OrderCostLine) => (
        <div title={row}>
          {record.code}. {row}
        </div>
      ),
    },
    {
      title: "Chi phí dự kiến",
      dataIndex: ["values", "estimated", "value"],
      render: (value: any, record: OrderCostLine) => (
        <InputNumber
          style={{ width: "100%" }}
          value={value}
          onChange={(nextValue: any) =>
            onChangeDetail(record.configId, ["values", "estimated", "value"], nextValue)
          }
          disabled={!canEditIncurred || !record?.values?.estimated?.editable}
          formatter={(value: number) =>
            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
        />
      ),
    },
    {
      title: "Chi phí thực tế",
      dataIndex: ["values", "actual", "value"],
      render: (value: any, record: OrderCostLine) => (
        <InputNumber
          style={{ width: "100%" }}
          value={value}
          onChange={(nextValue: any) =>
            onChangeDetail(record.configId, ["values", "actual", "value"], nextValue)
          }
          disabled={!canEditIncurred || !record?.values?.actual?.editable}
          formatter={(value: number) =>
            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
        />
      ),
    },
    {
      title: "Doanh thu bổ sung",
      dataIndex: ["values", "additionalRevenue", "value"],
      render: (value: any, record: OrderCostLine) => (
        <InputNumber
          style={{ width: "100%" }}
          value={value}
          onChange={(nextValue: any) =>
            onChangeDetail(
              record.configId,
              ["values", "additionalRevenue", "value"],
              nextValue,
            )
          }
          disabled={
            !canEditIncurred ||
            !record?.values?.additionalRevenue?.editable
          }
          formatter={(value: number) =>
            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
        />
      ),
    },
    {
      title: "Ghi chú",
      dataIndex: "note",
      render: (_value: any, record: OrderCostLine) => (
        <Input
          value={record.note ?? ""}
          onChange={(event) => onChangeDetail(record.configId, ["note"], event)}
          disabled={!canEditIncurred || dataDetail?.state !== "INIT"}
        />
      ),
    },
    {
      title: "Duyệt sale",
      dataIndex: ["reviews", "sale"],
      render: (row: any = {}, record: OrderCostLine) =>
        isOrderCostReviewableLine(record) ? (
          <div>
            <MultiStateCheckbox
              value={row.status}
              allowedStatuses={saleAllowedStatuses}
              disabled={
                dataDetail?.state !== "WAITING_APPROVE_L1" ||
                (!canApproveSale && !canRejectReview)
              }
              onChange={(nextValue) =>
                onChangeDetail(
                  record.configId,
                  ["reviews", "sale", "status"],
                  nextValue || "PENDING",
                )
              }
            />
            {renderReviewNoteInput(record, "sale", row.status)}
            <br />
            {row.reviewedBy &&
              `${dayjs(row.reviewedAt).format("DD/MM/YYYY HH:mm")} - (${row.reviewedBy?.accountName})`}
          </div>
        ) : (
          ""
        ),
    },
    {
      title: "Duyệt manager",
      dataIndex: ["reviews", "manager"],
      render: (row: any = {}, record: OrderCostLine) =>
        isOrderCostReviewableLine(record) ? (
          <div>
            <MultiStateCheckbox
              value={row.status}
              allowedStatuses={managerAllowedStatuses}
              disabled={
                dataDetail?.state !== "WAITING_APPROVE_L2" ||
                (!canApproveManager && !canRejectReview)
              }
              onChange={(nextValue) =>
                onChangeDetail(
                  record.configId,
                  ["reviews", "manager", "status"],
                  nextValue || "PENDING",
                )
              }
            />
            {renderReviewNoteInput(record, "manager", row.status)}
            <br />
            {row.reviewedBy &&
              `${dayjs(row.reviewedAt).format("DD/MM/YYYY HH:mm")} - (${row.reviewedBy?.accountName})`}
          </div>
        ) : (
          ""
        ),
    },
  ];

  const renderFooter = () => (
    <Row justify="end">
      <>
        <Button
          className={footerButtonClassName}
          onClick={onClose}
          disabled={savingDetail}
          style={{ backgroundColor: "#5C5C5C", color: "#fff" }}
        >
          Hủy thay đổi
        </Button>
        <Button
          className={footerButtonClassName}
          type="primary"
          style={{ backgroundColor: "#00529F" }}
          onClick={onSaveDetail}
          loading={savingDetail}
          disabled={savingDetail || !canEditIncurred}
        >
          Lưu thay đổi
        </Button>
      </>
      {showWorkflowActions && dataDetail.state === "INIT" && (
        <Button
          className={footerButtonClassName}
          type="primary"
          style={{
            backgroundColor: !canEditIncurred
              ? "#C0C0C0"
              : "#0082FB",
          }}
          onClick={onSubmitLevel1}
          disabled={!canEditIncurred}
        >
          Chuyển duyệt
        </Button>
      )}
      {showWorkflowActions && dataDetail.state === "WAITING_APPROVE_L1" && (
        <Button
          className={footerButtonClassName}
          type="primary"
          style={{
            backgroundColor: !canSubmitSaleReview
              ? "#C0C0C0"
              : "#0082FB",
          }}
          onClick={() => onReview(1, "sale")}
          disabled={!canSubmitSaleReview}
        >
          Duyệt
        </Button>
      )}
      {showWorkflowActions && dataDetail.state === "WAITING_APPROVE_L2" && (
        <Button
          className={footerButtonClassName}
          type="primary"
          style={{
            backgroundColor: !canSubmitManagerReview
              ? "#C0C0C0"
              : "#00529F",
          }}
          onClick={() => onReview(2, "manager")}
          disabled={!canSubmitManagerReview}
        >
          Duyệt
        </Button>
      )}
    </Row>
  );

  return (
    <Table
      className={detailTableDisabledStyles.ReadableDisabledDetailTable}
      columns={detailColumns as any}
      size="small"
      dataSource={dataDetail?.lines ?? []}
      rowKey={(record: any) => String(record.configId)}
      rowClassName={(record: OrderCostLine) => getReviewRowClassName(record)}
      pagination={false}
      loading={loadingDetail || savingDetail}
      scroll={{ x: "max-content", y: 500 }}
      style={{ width: "100%", marginTop: 16 }}
      footer={renderFooter}
      expandable={{
        defaultExpandAllRows: true,
      }}
    />
  );
};
